import React, { useState, useEffect } from 'react';
import WompiPagoWidget from './WompiPago'
import { 
  ShieldCheck, 
  Calendar,
  Clock, 
  ArrowRight, 
  Wallet,
  AlertTriangle,
  RefreshCw,
  Landmark,
  Ban,
} from 'lucide-react';
import { formatearPrecio } from '../../../../utils/utils';
import ModalTransferencia from './TransferenciaPago';

const ContainerPagos = ({
    precioACobrar,
    categoria, // "SOLICITUD" o "SERVICIO"
    idReferencia,
    sumaPagosClientes,
    nombreEmpresa,
}) => {
    const [modalTransferenciaOpen, setModalTransferenciaOpen] = useState(false);

    // --- LÓGICA DE SALDOS ---
    const deudaRestante = precioACobrar - sumaPagosClientes;
    const abonoMinimo = precioACobrar * 0.5;
    
    // Lo que le falta para llegar al primer 50% (solo para Solicitudes)
    const faltaParaAbono = abonoMinimo - sumaPagosClientes;

    const esSolicitud = categoria === "SOLICITUD";
    const esServicio = categoria === "SERVICIO";

    const tieneAbonoSuficiente = sumaPagosClientes >= abonoMinimo;
    const estaTotalizado = sumaPagosClientes >= precioACobrar;

    // Condición para mostrar el bloqueo de agenda
    const mostrarBloqueoAgenda = esSolicitud && tieneAbonoSuficiente && !estaTotalizado;

    return (
        <div className="w-full">
            
            {/* 1. CASO: PAGO TOTAL COMPLETADO */}
            {estaTotalizado ? (
                <div className="bg-emerald-50 border-2 border-emerald-100 rounded-[24px] p-8 flex flex-col items-center text-center shadow-sm">
                    <div className="bg-emerald-500 p-4 rounded-full text-white mb-4 shadow-lg shadow-emerald-200">
                        <ShieldCheck size={32} />
                    </div>
                    <h3 className="text-xl font-black text-emerald-900 uppercase tracking-tight text-center">¡Servicio al día!</h3>
                    <p className="text-emerald-700 text-sm font-medium mt-1">
                        Has completado el pago total de este servicio.
                    </p>
                </div>
            ) 
            
            /* 2. CASO: PROCESO DE AGENDA (Solo Solicitud al llegar al 50%) */
            : mostrarBloqueoAgenda ? (
                <div className="bg-blue-50 border-2 border-blue-100 rounded-[24px] p-8 flex flex-col items-center text-center shadow-sm">
                    <div className="bg-blue-600 p-4 rounded-full text-white mb-4 shadow-lg shadow-blue-200 animate-pulse">
                        <Calendar size={32} />
                    </div>
                    <h3 className="text-xl font-black text-blue-900 uppercase tracking-tight">Proceso de Agenda</h3>
                    <p className="text-blue-700 text-sm font-medium mt-2 max-w-md">
                        ¡Abono recibido! Estamos gestionando tu agenda.
                    </p>
                    <div className="mt-6 flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-blue-200 text-blue-600 text-[10px] font-black uppercase tracking-widest">
                        <Clock size={14} /> Estado: Verificando disponibilidad
                    </div>
                </div>
            ) 
            
            /* 3. CASO: MOSTRAR BOTONES (COBRANDO LA DEUDA) */
            : (
                <div className="space-y-6">
                    {/* Resumen de Deuda */}
                    <div className="bg-slate-900 rounded-2xl p-4 flex justify-between items-center text-white shadow-xl shadow-slate-200">
                        <div className="flex items-center gap-3">
                            <div className="bg-white/10 p-2 rounded-lg text-white">
                                <Wallet size={20} />
                            </div>
                            <span className="text-[10px] font-black uppercase tracking-widest opacity-60">Tu saldo pendiente</span>
                        </div>
                        <span className="text-xl font-black">{formatearPrecio(deudaRestante)}</span>
                    </div>

                    <div className={`grid grid-cols-1 ${esServicio ? 'sm:grid-cols-2' : 'sm:grid-cols-3'} gap-4`}>
                        
                        {/* Botón Abono (Solo para Solicitud y si aún no llega al 50%) */}
                        {esSolicitud && faltaParaAbono > 0 && (
                            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center hover:border-blue-300 transition-colors">
                                <p className="text-[10px] text-slate-400 mb-3 font-black uppercase tracking-widest">Abono 50%</p>
                                <WompiPagoWidget 
                                    valor={faltaParaAbono}
                                    referencia={`${categoria}_${idReferencia}_ABONO_${Date.now()}`}
                                    onSuccess={(datosWompi) => console.log("Abono exitoso", datosWompi)}
                                    titulo={`Pagar ${formatearPrecio(faltaParaAbono)}`}
                                />
                            </div>
                        )}

                        {/* Botón Pago Total (Cobra la deudaRestante) */}
                        <div className="bg-emerald-50 p-5 rounded-2xl border border-emerald-100 shadow-sm flex flex-col items-center hover:border-emerald-300 transition-colors">
                            <p className="text-[10px] text-emerald-600 mb-3 font-black uppercase tracking-widest italic">
                                {esServicio ? "Liquidar Servicio" : "Liquidar Total"}
                            </p>
                            <WompiPagoWidget 
                                valor={deudaRestante}
                                referencia={`${categoria}_${idReferencia}_TOTAL_${Date.now()}`}
                                onSuccess={(datosWompi) => console.log("Liquidación exitosa", datosWompi)}
                                titulo={`Pagar ${formatearPrecio(deudaRestante)}`}
                            />
                        </div>

                        {/* Botón Transferencia Manual */}
                        <div className="bg-blue-600 p-5 rounded-2xl shadow-lg shadow-blue-200 flex flex-col items-center justify-center hover:bg-blue-700 transition-all active:scale-95 cursor-pointer group"
                             onClick={() => setModalTransferenciaOpen(true)}>
                            <p className="text-[10px] text-blue-100 mb-3 font-black uppercase tracking-widest">Manual</p>
                            <div className="flex items-center gap-2 text-white font-bold text-sm">
                                <Landmark size={18} />
                                <span>Transferencia</span>
                                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* BARRA DE PROGRESO */}
            <div className="mt-8 px-2">
                <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                    <div 
                        className={`h-full transition-all duration-1000 ${estaTotalizado ? 'bg-emerald-500' : 'bg-blue-600'}`}
                        style={{ width: `${Math.min(100, (sumaPagosClientes / precioACobrar) * 100)}%` }}
                    ></div>
                </div>
                <div className="flex justify-between mt-2">
                    <span className="text-[9px] font-black text-slate-400 uppercase">Pagado: {formatearPrecio(sumaPagosClientes)}</span>
                    <span className="text-[9px] font-black text-slate-400 uppercase">Total: {formatearPrecio(precioACobrar)}</span>
                </div>
            </div>

            <ModalTransferencia 
                key={`modal-pago-${idReferencia}`}
                isOpen={modalTransferenciaOpen}
                onClose={() => setModalTransferenciaOpen(false)}
                precioACobrar={deudaRestante} // Pasamos la deuda para que no pague de más
                nombreEmpresa={nombreEmpresa}
                identificador={idReferencia}
            />
        </div>
    );
  
}

export default ContainerPagos
